import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import './StudentHomeworkDoPage.css'
import HomeworkAnnotationViewer from '../../components/homework/HomeworkAnnotationViewer'
import HtmlWithMermaid from '../../components/learning/HtmlWithMermaid'
import DrawioEmbedModal, {
  DrawioEmbedHandle,
  DrawioSaveResult,
} from '../../components/homework/drawio/DrawioEmbedModal'
import '../../components/homework/drawio/DrawioEmbedModal.css'
import TipTapRichTextEditor, {
  TipTapRichTextEditorHandle,
} from '../../components/editor/TipTapRichTextEditor'
import '../../components/editor/TipTapRichTextEditor.css'
import {
  DrawioBlockValue,
  appendDrawioBlockToHtml,
  replaceDrawioBlockInHtml,
} from '../../components/homework/drawio/drawioHtml'
import { HomeworkApi, HomeworkComment, HomeworkTocItem } from '../../services/homeworkApi'
import apiClient from '../../services/apiClient'
import { scrollToHomeworkHeading } from '../../utils/homeworkToc'

async function uploadStudentImage(file: File): Promise<string> {
  const form = new FormData()
  form.append('file', file)
  const res = await apiClient.post('/student/uploads/image', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
  const url = res.data?.data?.url
  if (!url) throw new Error('Upload thất bại')
  return url
}

function extractTocClient(html: string): HomeworkTocItem[] {
  if (!html?.trim() || typeof DOMParser === 'undefined') return []
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const headings = Array.from(doc.querySelectorAll('h1,h2,h3,h4,h5,h6'))
    const used = new Set<string>()
    return headings.map((h, i) => {
      const level = Number(h.tagName.replace('H', '')) || 1
      const text = (h.textContent || '').trim() || `Mục ${i + 1}`
      let id = h.id || ''
      if (!id) {
        id = `toc-${i}-${text
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/gi, '')
          .trim()
          .replace(/\s+/g, '-')
          .slice(0, 40) || 'section'}`
      }
      while (used.has(id)) id = `${id}-${i}`
      used.add(id)
      return { id, level, text }
    })
  } catch {
    return []
  }
}

function parseToc(raw: unknown): HomeworkTocItem[] {
  if (Array.isArray(raw)) return raw as HomeworkTocItem[]
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
  return []
}

function commentCardClass(c: HomeworkComment, active: boolean) {
  const base = 'border rounded-lg p-3 transition-colors'
  if (active) {
    return `${base} border-amber-500 bg-amber-50 ring-2 ring-amber-300/60`
  }
  if (c.status === 'resolved') {
    return `${base} border-emerald-200 bg-emerald-50/70`
  }
  return `${base} border-orange-200 bg-orange-50/80`
}

function sourceBadgeClass(source: HomeworkComment['source']) {
  if (source === 'ai') return 'bg-violet-100 text-violet-800'
  if (source === 'student') return 'bg-sky-100 text-sky-800'
  return 'bg-slate-800 text-white'
}

function statusBadgeClass(status: HomeworkComment['status']) {
  if (status === 'resolved') return 'bg-emerald-600 text-white'
  return 'bg-orange-500 text-white'
}

type CenterMode = 'edit' | 'review'

export default function StudentHomeworkDoPage() {
  const { courseId, homeworkId } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [name, setName] = useState('')
  const [assignmentHtml, setAssignmentHtml] = useState('')
  const [homeworkType, setHomeworkType] = useState<'submission' | 'self_practice'>('submission')
  const [allowCompletionNote, setAllowCompletionNote] = useState(true)
  const [completionNote, setCompletionNote] = useState('')
  /** Nội dung đang soạn trong TipTap */
  const [contentHtml, setContentHtml] = useState('')
  /** Ref đồng bộ nội dung — tránh re-render controlled mỗi keystroke */
  const contentHtmlRef = useRef('')
  /** Bản đã lưu — dùng để bôi vàng comment (neo đúng HTML) */
  const [savedHtml, setSavedHtml] = useState('')
  const [editable, setEditable] = useState(true)
  const [submissionId, setSubmissionId] = useState<number | null>(null)
  const [status, setStatus] = useState<string>('draft')
  const [aiScanStatus, setAiScanStatus] = useState<string>('idle')
  const [comments, setComments] = useState<HomeworkComment[]>([])
  const [toc, setToc] = useState<HomeworkTocItem[]>([])
  const [activeCommentId, setActiveCommentId] = useState<number | null>(null)
  const [replyDraft, setReplyDraft] = useState('')
  const [replyParentId, setReplyParentId] = useState<number | null>(null)
  const [saving, setSaving] = useState(false)
  const [tocCollapsed, setTocCollapsed] = useState(false)
  const [rightCollapsed, setRightCollapsed] = useState(false)
  const [centerMode, setCenterMode] = useState<CenterMode>('edit')
  const [domToc, setDomToc] = useState<HomeworkTocItem[]>([])
  const [pendingJump, setPendingJump] = useState<{ id: string; text?: string } | null>(null)
  const [assignmentSheetOpen, setAssignmentSheetOpen] = useState(false)
  const editorRef = useRef<TipTapRichTextEditorHandle | null>(null)
  const drawioRef = useRef<DrawioEmbedHandle | null>(null)
  const [drawioOpen, setDrawioOpen] = useState(false)
  const [drawioCloseConfirm, setDrawioCloseConfirm] = useState(false)
  const [drawioInitial, setDrawioInitial] = useState<
    (Partial<DrawioBlockValue> & { xml?: string }) | undefined
  >(undefined)
  const tocSyncTimerRef = useRef<number | null>(null)
  const savedHtmlRef = useRef('')

  /** Update ref immediately; debounce React state for TOC (không remount editor) */
  const syncContentFromEditor = useCallback((html: string, opts?: { immediate?: boolean }) => {
    contentHtmlRef.current = html
    if (opts?.immediate) {
      if (tocSyncTimerRef.current != null) {
        window.clearTimeout(tocSyncTimerRef.current)
        tocSyncTimerRef.current = null
      }
      setContentHtml(html)
      return
    }
    if (tocSyncTimerRef.current != null) window.clearTimeout(tocSyncTimerRef.current)
    tocSyncTimerRef.current = window.setTimeout(() => {
      tocSyncTimerRef.current = null
      setContentHtml(contentHtmlRef.current)
    }, 400)
  }, [])

  const onTocReady = useCallback((next: HomeworkTocItem[]) => {
    setDomToc(next)
  }, [])

  useEffect(() => {
    if (!assignmentSheetOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [assignmentSheetOpen])

  const applyPayload = useCallback(
    (data: any, opts?: { resetContent?: boolean; preferReview?: boolean }) => {
      setName(data.name)
      setAssignmentHtml(data.contentHtml || '')
      setHomeworkType(data.homeworkType === 'self_practice' ? 'self_practice' : 'submission')
      setAllowCompletionNote(Boolean(data.allowCompletionNote ?? 1))
      setEditable(Boolean(data.editable))
      setComments(data.comments || [])
      setCompletionNote(data.submission?.completionNote || '')

      if (data.homeworkType === 'self_practice') {
        if (data.submission) {
          setSubmissionId(data.submission.id)
          setStatus(data.submission.status)
        } else {
          setSubmissionId(null)
          setStatus('draft')
        }
        return
      }

      if (data.submission) {
        setSubmissionId(data.submission.id)
        const existing = data.submission.contentHtml || ''
        const isEmpty = !existing.trim() || existing.replace(/<[^>]+>/g, '').trim() === ''
        let nextHtml = existing
        if (
          isEmpty &&
          data.editable &&
          (data.submission.status === 'draft' || data.submission.status === 'returned') &&
          data.templateHtml
        ) {
          nextHtml = data.templateHtml
        }
        if (opts?.resetContent !== false) {
          setContentHtml(nextHtml)
          contentHtmlRef.current = nextHtml
          const baseline = existing || nextHtml
          setSavedHtml(baseline)
          savedHtmlRef.current = baseline
        }
        setStatus(data.submission.status)
        setAiScanStatus(data.submission.aiScanStatus)
        setToc(parseToc(data.submission.tocJson))

        const cmts: HomeworkComment[] = data.comments || []
        const hasAnchored = cmts.some((c) => !c.parentId && c.anchorType)
        if (opts?.preferReview || (!data.editable && hasAnchored)) {
          setCenterMode('review')
        } else if (data.editable && (data.submission.status === 'returned' || hasAnchored)) {
          setCenterMode('review')
        } else if (data.editable) {
          setCenterMode('edit')
        } else {
          setCenterMode('review')
        }
      } else {
        const tpl = data.templateHtml || ''
        if (opts?.resetContent !== false) {
          setContentHtml(tpl)
          contentHtmlRef.current = tpl
          setSavedHtml(tpl)
          savedHtmlRef.current = tpl
        }
        setStatus('draft')
        setSubmissionId(null)
        setToc([])
        setCenterMode(data.editable ? 'edit' : 'review')
      }
    },
    []
  )

  const load = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!courseId || !homeworkId) return
      try {
        if (!opts?.silent) setLoading(true)
        const data = await HomeworkApi.studentGet(Number(courseId), Number(homeworkId))
        if (opts?.silent) {
          setComments(data.comments || [])
          setEditable(Boolean(data.editable))
          if (data.submission) {
            setStatus(data.submission.status)
            setAiScanStatus(data.submission.aiScanStatus)
            setSubmissionId(data.submission.id)
            setToc(parseToc(data.submission.tocJson))
          }
        } else {
          applyPayload(data, { resetContent: true })
        }
      } catch (e: any) {
        toast.error(e?.message || 'Không tải được BTVN')
      } finally {
        if (!opts?.silent) setLoading(false)
      }
    },
    [courseId, homeworkId, applyPayload]
  )

  useEffect(() => {
    void load()
  }, [load])

  const refreshCommentsSilent = useCallback(async () => {
    if (!courseId || !homeworkId) return
    try {
      const data = await HomeworkApi.studentGet(Number(courseId), Number(homeworkId))
      setComments(data.comments || [])
      setEditable(Boolean(data.editable))
      if (data.submission) {
        setStatus(data.submission.status)
        setAiScanStatus(data.submission.aiScanStatus)
        setSubmissionId(data.submission.id)
      }
    } catch {
      /* ignore */
    }
  }, [courseId, homeworkId])

  const displayToc = useMemo(() => {
    if (centerMode === 'review' && domToc.length > 0) return domToc
    if (toc.length > 0 && centerMode === 'review') return toc
    const htmlForToc = centerMode === 'edit' ? contentHtml : savedHtml || contentHtml
    const live = extractTocClient(htmlForToc)
    return live.length > 0 ? live : toc
  }, [toc, contentHtml, savedHtml, centerMode, domToc])

  useEffect(() => {
    if (centerMode !== 'review' || !pendingJump) return
    let tries = 0
    const tick = () => {
      const ok = scrollToHomeworkHeading(pendingJump.id, {
        text: pendingJump.text,
        rootSelector: '.homework-annotation-body',
      })
      tries += 1
      if (ok || tries >= 8) {
        setPendingJump(null)
        return
      }
      window.setTimeout(tick, 60)
    }
    const t = window.setTimeout(tick, 40)
    return () => window.clearTimeout(t)
  }, [centerMode, pendingJump, savedHtml, contentHtml, domToc])

  const openDrawioNew = useCallback(() => {
    setDrawioInitial(undefined)
    setDrawioOpen(true)
  }, [])

  const openDrawioEdit = useCallback((value: DrawioBlockValue) => {
    setDrawioInitial({
      diagramId: value.diagramId,
      pngUrl: value.pngUrl,
      xmlUrl: value.xmlUrl,
      title: value.title,
      version: value.version,
    })
    setDrawioOpen(true)
  }, [])

  const onDrawioRequest = useCallback(
    (mode: 'new' | 'edit', value?: DrawioBlockValue) => {
      if (mode === 'edit' && value) openDrawioEdit(value)
      else openDrawioNew()
    },
    [openDrawioEdit, openDrawioNew]
  )

  const onDrawioSaved = useCallback(
    async (result: DrawioSaveResult) => {
      const block: DrawioBlockValue = {
        diagramId: result.diagramId,
        pngUrl: result.pngUrl,
        xmlUrl: result.xmlUrl,
        title: result.title || 'Sơ đồ quy trình',
        version: result.version || 1,
      }

      const inEditor = editable && centerMode === 'edit'
      let html = ''
      if (inEditor && editorRef.current) {
        if (drawioInitial?.diagramId) {
          editorRef.current.updateDrawio(block)
        } else {
          editorRef.current.insertDrawio(block)
        }
        html = editorRef.current.getHtml()
        syncContentFromEditor(html, { immediate: true })
      } else {
        const prev = contentHtmlRef.current || contentHtml
        if (drawioInitial?.diagramId) {
          const replaced = replaceDrawioBlockInHtml(prev, block)
          html = replaced.replaced ? replaced.html : appendDrawioBlockToHtml(prev, block)
        } else {
          html = appendDrawioBlockToHtml(prev, block)
        }
        contentHtmlRef.current = html
        setContentHtml(html)
      }

      setDrawioOpen(false)
      setDrawioCloseConfirm(false)
      setDrawioInitial(undefined)

      if (!editable || !courseId || !homeworkId) {
        toast.info('Sơ đồ đã cập nhật trong bài — nhớ Lưu nháp / Nộp bài')
        return
      }

      try {
        setSaving(true)
        const sub = await HomeworkApi.studentSaveDraft(
          Number(courseId),
          Number(homeworkId),
          html
        )
        setSubmissionId(sub.id)
        setStatus(sub.status)
        setSavedHtml(html)
        savedHtmlRef.current = html
        setContentHtml(html)
        contentHtmlRef.current = html
        setToc(parseToc(sub.tocJson))
        toast.success('Đã lưu nháp (kèm sơ đồ)')
      } catch (e: any) {
        toast.error(
          e?.message ||
            'Sơ đồ đã gắn vào bài nhưng lưu nháp thất bại — hãy bấm Lưu nháp ngay'
        )
      } finally {
        setSaving(false)
      }
    },
    [
      editable,
      centerMode,
      drawioInitial?.diagramId,
      syncContentFromEditor,
      contentHtml,
      courseId,
      homeworkId,
    ]
  )

  const closeDrawioWithoutSave = useCallback(() => {
    setDrawioCloseConfirm(false)
    setDrawioOpen(false)
    setDrawioInitial(undefined)
  }, [])

  const requestCloseDrawio = useCallback(() => {
    if (saving) return
    if (drawioRef.current?.isDirty()) {
      setDrawioCloseConfirm(true)
      return
    }
    closeDrawioWithoutSave()
  }, [closeDrawioWithoutSave, saving])

  const confirmSaveAndCloseDrawio = useCallback(async () => {
    try {
      await drawioRef.current?.save(false)
      // onDrawioSaved closes modal + auto draft
      setDrawioCloseConfirm(false)
    } catch {
      /* toast already shown in modal */
    }
  }, [])

  useEffect(() => {
    return () => {
      if (tocSyncTimerRef.current != null) {
        window.clearTimeout(tocSyncTimerRef.current)
        tocSyncTimerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    if (!editable) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      const live =
        editorRef.current?.getHtml?.() ||
        contentHtmlRef.current ||
        ''
      if (live !== savedHtmlRef.current) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [editable])

  const liveEditorHtml = () => {
    const fromRef = editorRef.current?.getHtml?.()
    return (fromRef && fromRef.trim() ? fromRef : null) || contentHtmlRef.current || contentHtml
  }

  const saveDraft = async () => {
    if (!courseId || !homeworkId) return
    const html = liveEditorHtml()
    contentHtmlRef.current = html
    try {
      setSaving(true)
      const sub = await HomeworkApi.studentSaveDraft(
        Number(courseId),
        Number(homeworkId),
        html
      )
      setSubmissionId(sub.id)
      setStatus(sub.status)
      setSavedHtml(html)
      savedHtmlRef.current = html
      setContentHtml(html)
      setToc(parseToc(sub.tocJson))
      toast.success('Đã lưu nháp')
    } catch (e: any) {
      toast.error(e?.message || 'Lưu thất bại')
    } finally {
      setSaving(false)
    }
  }

  const submit = async () => {
    if (!courseId || !homeworkId) return
    if (!confirm('Nộp bài? Sau khi nộp bạn không thể sửa cho đến khi được trả bài.')) return
    const html = liveEditorHtml()
    contentHtmlRef.current = html
    try {
      setSaving(true)
      const sub = await HomeworkApi.studentSubmit(
        Number(courseId),
        Number(homeworkId),
        html
      )
      setSubmissionId(sub.id)
      setStatus(sub.status)
      setEditable(false)
      setSavedHtml(html)
      savedHtmlRef.current = html
      setContentHtml(html)
      setToc(parseToc(sub.tocJson))
      setCenterMode('review')
      toast.success('Đã nộp bài')
      await refreshCommentsSilent()
    } catch (e: any) {
      toast.error(e?.message || 'Nộp thất bại')
    } finally {
      setSaving(false)
    }
  }

  const selfComplete = async () => {
    if (!courseId || !homeworkId) return
    try {
      setSaving(true)
      const sub = await HomeworkApi.studentSelfComplete(
        Number(courseId),
        Number(homeworkId),
        allowCompletionNote ? completionNote : null
      )
      setSubmissionId(sub.id)
      setStatus(sub.status)
      setEditable(false)
      setCompletionNote(sub.completionNote || '')
      toast.success('Đã đánh dấu hoàn thành')
    } catch (e: any) {
      toast.error(e?.message || 'Không thể đánh dấu hoàn thành')
    } finally {
      setSaving(false)
    }
  }

  const focusComment = (id: number) => {
    setActiveCommentId(id)
    setRightCollapsed(false)
    setCenterMode('review')
  }

  const sendReply = async (parentId: number, body: string) => {
    if (!submissionId || !body.trim()) return
    const created = await HomeworkApi.studentComment(submissionId, body.trim(), parentId)
    setComments((prev) => [...prev, created])
    setReplyDraft('')
    setReplyParentId(null)
    toast.success('Đã gửi reply')
  }

  const toggleResolve = async (c: HomeworkComment) => {
    const next = c.status === 'resolved' ? 'open' : 'resolved'
    setComments((prev) =>
      prev.map((x) => (x.id === c.id ? { ...x, status: next as HomeworkComment['status'] } : x))
    )
    try {
      await HomeworkApi.studentResolve(c.id, next)
    } catch (e: any) {
      setComments((prev) =>
        prev.map((x) => (x.id === c.id ? { ...x, status: c.status } : x))
      )
      toast.error(e?.message || 'Cập nhật thất bại')
    }
  }

  const roots = comments.filter((c) => !c.parentId)
  const repliesOf = (id: number) => comments.filter((c) => Number(c.parentId) === id)
  const hasComments = roots.length > 0

  const jumpToc = (id: string, text?: string) => {
    if (centerMode !== 'review') {
      setCenterMode('review')
      setPendingJump({ id, text })
      return
    }
    const ok = scrollToHomeworkHeading(id, {
      text,
      rootSelector: '.homework-annotation-body',
    })
    if (!ok) setPendingJump({ id, text })
  }

  if (loading) {
    return <div className="p-8 text-m2-on-surface-variant">Đang tải...</div>
  }

  if (homeworkType === 'self_practice') {
    const isDone = status === 'completed'
    return (
      <div className="student-homework-do max-w-3xl mx-auto p-4 lg:p-8">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="text-sm text-m2-primary hover:underline mb-3"
        >
          ← Quay lại
        </button>
        <div className="flex flex-wrap items-start justify-between gap-2 mb-4">
          <div>
            <h1 className="text-2xl font-bold text-m2-on-surface">{name}</h1>
            <p className="text-xs text-m2-on-surface-variant mt-1">BTVN tự thực hành</p>
          </div>
          <span
            className={`text-xs font-medium px-2 py-1 rounded-full ${
              isDone
                ? 'bg-emerald-100 text-emerald-800'
                : 'bg-m2-surface-high text-m2-on-surface-variant'
            }`}
          >
            {isDone ? 'Hoàn thành' : 'Chưa làm'}
          </span>
        </div>

        <div className="prose prose-sm max-w-none border border-m2-outline-variant rounded-xl p-4 mb-6 bg-m2-surface-container">
          <HtmlWithMermaid html={assignmentHtml || '<p>Chưa có nội dung đề.</p>'} />
        </div>

        {allowCompletionNote && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">
              Ghi chú khi hoàn thành {isDone ? '(có thể cập nhật)' : '(tuỳ chọn)'}
            </label>
            <textarea
              value={completionNote}
              onChange={(e) => setCompletionNote(e.target.value)}
              rows={4}
              className="w-full border border-m2-outline-variant rounded-md px-3 py-2 text-sm"
              placeholder="Ghi lại kết quả / ghi chú của bạn..."
            />
          </div>
        )}

        <button
          type="button"
          disabled={saving || (isDone && !allowCompletionNote)}
          onClick={() => void selfComplete()}
          className="px-4 py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
        >
          {saving
            ? 'Đang lưu...'
            : isDone
              ? allowCompletionNote
                ? 'Cập nhật ghi chú / xác nhận lại'
                : 'Đã hoàn thành'
              : 'Đánh dấu hoàn thành'}
        </button>
      </div>
    )
  }

  const showEditor = editable && centerMode === 'edit'
  const annotationHtml = editable
    ? contentHtml || savedHtml
    : savedHtml || contentHtml

  return (
    <div className="student-hw-do w-full max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 py-4 lg:py-5">
      <button
        type="button"
        className="text-sm text-m2-primary mb-2"
        onClick={() => navigate('/student/homeworks')}
      >
        ← Quay lại danh sách BTVN
      </button>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-m2-on-surface">{name}</h1>
          <p className="text-sm text-m2-on-surface-variant mt-1">
            Trạng thái: {status}
            {aiScanStatus && status !== 'draft' ? ` · AI: ${aiScanStatus}` : ''}
            {!editable ? ' · Đã khóa chỉnh sửa' : ''}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {editable && (
            <>
              <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-sm bg-white">
                <button
                  type="button"
                  className={`px-3 py-1.5 font-medium ${
                    centerMode === 'review'
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                  onClick={() => setCenterMode('review')}
                >
                  Xem + comment
                </button>
                <button
                  type="button"
                  className={`px-3 py-1.5 font-medium border-l border-slate-300 ${
                    centerMode === 'edit'
                      ? 'bg-sky-600 text-white'
                      : 'text-slate-700 hover:bg-slate-100'
                  }`}
                  onClick={() => setCenterMode('edit')}
                >
                  Sửa bài
                </button>
              </div>
              <button
                type="button"
                disabled={saving}
                onClick={() => void saveDraft()}
                className="px-4 py-2 rounded-md border border-slate-300 text-sm text-slate-700 bg-white hover:bg-slate-50"
              >
                Lưu nháp
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void submit()}
                className="px-4 py-2 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white text-sm font-medium"
              >
                Nộp bài
              </button>
            </>
          )}
        </div>
      </div>

      {editable && hasComments && centerMode === 'edit' && (
        <p className="mb-3 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Đang sửa bài nên tạm ẩn highlight. Bấm <strong>Xem + comment</strong> để thấy bôi vàng và
          nhảy tới từng nhận xét.
        </p>
      )}

      <div
        className={`grid grid-cols-1 gap-4 items-start ${
          tocCollapsed && rightCollapsed
            ? 'lg:grid-cols-[44px_minmax(0,1fr)_44px]'
            : tocCollapsed
              ? 'lg:grid-cols-[44px_minmax(0,1fr)_320px]'
              : rightCollapsed
                ? 'lg:grid-cols-[240px_minmax(0,1fr)_44px]'
                : 'lg:grid-cols-[240px_minmax(0,1fr)_320px]'
        }`}
      >
        {tocCollapsed ? (
          <aside className="hidden lg:flex sticky top-16 h-[calc(100vh-6rem)]">
            <button
              type="button"
              onClick={() => setTocCollapsed(false)}
              className="w-11 border rounded-xl bg-m2-surface-low hover:bg-m2-primary/10 text-m2-on-surface-variant text-xs flex items-center justify-center px-1"
              title="Mở mục lục"
              style={{ writingMode: 'vertical-rl' }}
            >
              Mục lục ▸
            </button>
          </aside>
        ) : (
          <aside className="border rounded-xl p-3 bg-m2-surface-low sticky top-16 max-h-[calc(100vh-6rem)] overflow-auto">
            <div className="flex items-center justify-between gap-2 mb-2">
              <h3 className="font-semibold text-sm">Mục lục</h3>
              <button
                type="button"
                onClick={() => setTocCollapsed(true)}
                className="text-xs px-2 py-1 rounded border border-m2-outline-variant hover:bg-white text-m2-on-surface-variant"
              >
                ◂ Thu hẹp
              </button>
            </div>
            {displayToc.length === 0 ? (
              <p className="text-xs text-m2-on-surface-variant">
                Chưa có heading. Dùng Heading 1/2/3 khi viết để tạo mục lục.
              </p>
            ) : (
              <ul className="space-y-1 text-sm">
                {displayToc.map((t) => (
                  <li key={t.id} style={{ paddingLeft: `${Math.max(0, t.level - 1) * 10}px` }}>
                    <button
                      type="button"
                      className="text-left text-m2-primary hover:underline w-full truncate"
                      title={t.text}
                      onClick={() => jumpToc(t.id, t.text)}
                    >
                      {t.text}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        )}

        <div className="border rounded-xl p-4 lg:p-6 bg-white min-h-[70vh]">
          <div className="flex lg:hidden gap-2 mb-3">
            <button
              type="button"
              className="text-xs px-2 py-1 rounded border"
              onClick={() => setTocCollapsed((v) => !v)}
            >
              {tocCollapsed ? 'Hiện mục lục' : 'Ẩn mục lục'}
            </button>
            <button
              type="button"
              className="text-xs px-2 py-1 rounded border"
              onClick={() => setRightCollapsed((v) => !v)}
            >
              {rightCollapsed ? 'Hiện panel' : 'Ẩn panel'}
            </button>
          </div>

          {showEditor ? (
            <>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={openDrawioNew}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-sky-600 text-white hover:bg-sky-700"
                >
                  + Vẽ sơ đồ (draw.io)
                </button>
                <span className="text-xs text-m2-on-surface-variant">
                  Hoặc nút Draw.io trên toolbar. Click preview để sửa lại.
                </span>
              </div>
              <TipTapRichTextEditor
                ref={editorRef}
                value={contentHtml}
                onChange={(html) => syncContentFromEditor(html)}
                uploadImage={uploadStudentImage}
                enableDrawio
                onDrawioRequest={onDrawioRequest}
                className="homework-tiptap-sticky homework-tiptap-readable"
                placeholder="Soạn bài làm…"
                minHeightClass="min-h-[50vh]"
              />
              <p className="mt-3 text-xs text-m2-on-surface-variant">
                Mermaid: bấm <strong>code block</strong> (&lt;/&gt;), dán mã bắt đầu bằng{' '}
                <code>flowchart</code> / <code>sequenceDiagram</code> / <code>graph</code>… · Draw.io:
                chèn sơ đồ để vẽ BPMN/quy trình, lưu bằng Save trong editor (không cần login).
              </p>
            </>
          ) : (
            <>
              {editable && (
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={openDrawioNew}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md bg-sky-600 text-white hover:bg-sky-700"
                  >
                    + Vẽ sơ đồ (draw.io)
                  </button>
                  <span className="text-xs text-m2-on-surface-variant">
                    Click vào sơ đồ đã có để sửa · comment text trên tên quy trình
                  </span>
                </div>
              )}
              <HomeworkAnnotationViewer
                html={annotationHtml}
                toc={displayToc}
                comments={comments}
                mode="view"
                hideToc
                bodyClassName="prose-lg lg:prose-xl student-hw-readable html-media-center"
                activeCommentId={activeCommentId}
                onSelectComment={focusComment}
                onTocReady={onTocReady}
                canReply={Boolean(submissionId)}
                canEditDiagram={editable}
                onEditDiagram={openDrawioEdit}
                onReplyToComment={async (parentId, body) => {
                  await sendReply(parentId, body)
                }}
              />
            </>
          )}
        </div>

        {rightCollapsed ? (
          <aside className="hidden lg:flex sticky top-16 h-[calc(100vh-6rem)] justify-end">
            <button
              type="button"
              onClick={() => setRightCollapsed(false)}
              className="w-11 border rounded-xl bg-m2-surface-low hover:bg-m2-primary/10 text-m2-on-surface-variant text-xs flex items-center justify-center px-1"
              style={{ writingMode: 'vertical-rl' }}
            >
              ◂ Comments
            </button>
          </aside>
        ) : (
          <aside className="border border-slate-200 rounded-xl bg-slate-50 sticky top-16 max-h-[calc(100vh-6rem)] flex flex-col overflow-hidden">
            <div className="flex items-center border-b border-slate-200 shrink-0 bg-white">
              <div className="flex-1 py-2.5 px-3 text-sm font-medium text-orange-700 border-b-2 border-orange-500 bg-orange-50/50">
                Comments ({roots.length})
              </div>
              <button
                type="button"
                onClick={() => setRightCollapsed(true)}
                className="hidden lg:inline-flex px-2 py-2 text-xs text-slate-500"
              >
                ▸
              </button>
            </div>

            <div className="p-3 overflow-auto space-y-3 flex-1">
              {!submissionId || roots.length === 0 ? (
                <p className="text-sm text-slate-500">Chưa có comment hiển thị.</p>
              ) : (
                roots.map((c) => (
                  <div
                    key={c.id}
                    id={`cmt-${c.id}`}
                    className={commentCardClass(c, activeCommentId === c.id)}
                  >
                    <button
                      type="button"
                      className="text-left w-full"
                      onClick={() => focusComment(c.id)}
                    >
                      <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                        <span
                          className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${sourceBadgeClass(c.source)}`}
                        >
                          {c.source === 'ai' ? 'AI' : c.source === 'student' ? 'HV' : 'Admin'}
                        </span>
                        <span
                          className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${statusBadgeClass(c.status)}`}
                        >
                          {c.status === 'resolved' ? 'Đã resolve' : 'Chưa resolve'}
                        </span>
                        {c.authorName && (
                          <span className="text-[11px] text-slate-500">{c.authorName}</span>
                        )}
                      </div>
                      <p className="text-sm whitespace-pre-wrap text-slate-900">{c.body}</p>
                    </button>
                    <div className="flex gap-3 mt-2 text-xs">
                      <button
                        type="button"
                        className="font-medium text-sky-700 hover:underline"
                        onClick={() => setReplyParentId(c.id)}
                      >
                        Reply
                      </button>
                      <button
                        type="button"
                        className={
                          c.status === 'resolved'
                            ? 'font-medium text-slate-600 hover:underline'
                            : 'font-medium text-orange-700 hover:underline'
                        }
                        onClick={() => void toggleResolve(c)}
                      >
                        {c.status === 'resolved' ? 'Mở lại' : 'Resolve'}
                      </button>
                    </div>
                    {repliesOf(c.id).map((r) => (
                      <div
                        key={r.id}
                        className="ml-2 mt-2 border-l-2 border-sky-300 pl-2 py-1 text-xs bg-sky-50/80 rounded-r"
                      >
                        <strong className="text-sky-800">{r.authorName || r.source}:</strong>{' '}
                        {r.body}
                      </div>
                    ))}
                    {replyParentId === c.id && (
                      <div className="mt-2">
                        <textarea
                          className="w-full border border-slate-300 rounded p-2 text-sm bg-white"
                          rows={2}
                          value={replyDraft}
                          onChange={(e) => setReplyDraft(e.target.value)}
                        />
                        <button
                          type="button"
                          className="mt-1 px-3 py-1 text-xs bg-sky-600 text-white rounded"
                          onClick={() => void sendReply(c.id, replyDraft)}
                        >
                          Gửi
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </aside>
        )}
      </div>

      {/* FAB xem đề bài */}
      <button
        type="button"
        onClick={() => setAssignmentSheetOpen(true)}
        className="student-hw-assignment-fab"
        title="Xem đề bài"
        aria-label="Xem đề bài"
      >
        <span className="text-lg leading-none" aria-hidden>
          📄
        </span>
        <span className="text-[11px] font-semibold">Đề bài</span>
      </button>

      {/* Sheet đề bài rộng, trượt lên */}
      {assignmentSheetOpen && (
        <div className="student-hw-assignment-sheet" role="dialog" aria-modal="true">
          <button
            type="button"
            className="student-hw-assignment-sheet__backdrop"
            aria-label="Đóng"
            onClick={() => setAssignmentSheetOpen(false)}
          />
          <div className="student-hw-assignment-sheet__panel">
            <div className="student-hw-assignment-sheet__handle" aria-hidden />
            <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 border-b border-m2-outline-variant shrink-0">
              <h2 className="text-lg font-bold text-m2-on-surface">Đề bài — {name}</h2>
              <button
                type="button"
                onClick={() => setAssignmentSheetOpen(false)}
                className="px-3 py-1.5 text-sm rounded-lg border border-m2-outline-variant hover:bg-m2-surface-low"
              >
                Đóng
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 sm:px-8 lg:px-12 py-5">
              <HtmlWithMermaid
                className="prose prose-lg lg:prose-xl max-w-4xl mx-auto student-hw-readable html-media-center"
                html={assignmentHtml}
                emptyHtml="<p class='text-m2-on-surface-variant italic'>Chưa có đề</p>"
              />
            </div>
          </div>
        </div>
      )}

      <DrawioEmbedModal
        ref={drawioRef}
        open={drawioOpen}
        onClose={closeDrawioWithoutSave}
        onRequestClose={requestCloseDrawio}
        initial={drawioInitial}
        defaultTitle="Sơ đồ quy trình"
        onSaved={onDrawioSaved}
        quietSave
      />

      {drawioCloseConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
          <div
            className="bg-white rounded-xl shadow-xl max-w-sm w-full p-5 space-y-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="drawio-close-title"
          >
            <h3 id="drawio-close-title" className="text-base font-semibold text-slate-900">
              Lưu sơ đồ trước khi đóng?
            </h3>
            <p className="text-sm text-slate-600">
              Có thay đổi chưa lưu. Chọn Lưu để upload sơ đồ và tự lưu nháp bài tập.
            </p>
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-md border border-slate-300 hover:bg-slate-50"
                onClick={() => setDrawioCloseConfirm(false)}
                disabled={saving}
              >
                Hủy
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-md border border-slate-300 text-red-700 hover:bg-red-50"
                onClick={closeDrawioWithoutSave}
                disabled={saving}
              >
                Không lưu
              </button>
              <button
                type="button"
                className="px-3 py-1.5 text-sm rounded-md bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
                onClick={() => void confirmSaveAndCloseDrawio()}
                disabled={saving}
              >
                {saving ? 'Đang lưu…' : 'Lưu'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
